// Sidebar navigation driven by app/nav.ts: permission-aware, sub-items expand for the active
// section, collapsible to an icon rail on desktop, rendered inside a drawer on mobile.
import { Link, useLocation } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PanelLeftClose, PanelLeftOpen, Sparkles } from 'lucide-react'
import { cn } from '@/components/ui/cn'
import { Tooltip } from '@/components/ui/tooltip'
import { Kbd } from '@/components/ui/kbd'
import { useSession } from '@/lib/session'
import { api } from '@/lib/api'
import { useStoreEvents } from '@/lib/hooks'
import type { NavSection, NavItem } from '@/app/nav'
import { NavIcon } from './icons'

function isActive(path: string, to: string) {
  if (to === '/') return path === '/'
  return path === to || path.startsWith(to + '/')
}

export interface SidebarProps {
  nav: NavSection[]
  collapsed?: boolean
  onToggleCollapsed?: () => void
  /** Called after a link is chosen (closes the mobile drawer). */
  onNavigate?: () => void
  /** Extra content pinned to the bottom (plan card). */
  footer?: React.ReactNode
  /** Drawer variant (mobile): always expanded, no collapse control. */
  drawer?: boolean
  header?: React.ReactNode
}

export function Sidebar({ nav, collapsed = false, onToggleCollapsed, onNavigate, footer, drawer, header }: SidebarProps) {
  const { pathname } = useLocation()
  const { can, store } = useSession()
  const allowed = (it: NavItem) => !it.perm || !store || can(it.perm)
  const rail = collapsed && !drawer
  // An item whose path prefixes a sibling's ("/platform" vs "/platform/stores") only matches exactly.
  const allTo = nav.flatMap(s => s.items.map(i => i.to))
  const exactOnly = (to: string) => allTo.some(o => o !== to && o.startsWith(to === '/' ? '/' : to + '/'))

  // Which child link is the active one (longest match wins, so "/online-store/pages" beats "/online-store").
  const activeChild = (it: NavItem) => {
    const matches = (it.children ?? []).filter(c => isActive(pathname, c.to))
    return matches.sort((a, b) => b.to.length - a.to.length)[0]?.to ?? null
  }

  return (
    <nav aria-label="Main" className={cn('flex h-full min-h-0 flex-col', rail ? 'w-14' : drawer ? 'w-full' : 'w-60')}>
      {header}
      <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden py-3', rail ? 'px-2' : 'px-3')}>
        {nav.map((section, si) => {
          const items = section.items.filter(allowed)
          if (!items.length) return null
          return (
            <div key={si} className="flex flex-col gap-px">
              {section.label && !rail && <div className="px-2 pb-1 text-xs font-medium text-text-subtle">{section.label}</div>}
              {section.label && rail && si > 0 && <div className="mx-2 mb-1 h-px bg-border" />}
              {items.map(it => {
                const child = activeChild(it)
                const inSection = (exactOnly(it.to) ? pathname === it.to : isActive(pathname, it.to)) || !!child
                const highlighted = inSection && !child   // on the parent (or its detail pages), not on a child link
                const link = (
                  <Link
                    to={it.to}
                    onClick={onNavigate}
                    aria-current={highlighted ? 'page' : undefined}
                    className={cn(
                      'group relative flex h-8 items-center gap-2 rounded-control text-base font-medium transition-[background-color,color] duration-150 outline-none',
                      'focus-visible:ring-2 focus-visible:ring-focus',
                      rail ? 'w-10 justify-center' : 'px-2',
                      highlighted ? 'bg-chrome-active text-text shadow-[0_1px_2px_rgb(0_0_0/0.06),0_0_0_1px_var(--border)] dark:shadow-[0_0_0_1px_var(--border)]'
                        : inSection ? 'text-text' : 'text-text-muted hover:bg-chrome-hover hover:text-text',
                    )}
                  >
                    <NavIcon name={it.icon} className={cn('size-[18px] shrink-0 transition-colors', inSection ? 'text-primary-strong' : 'text-text-subtle group-hover:text-text-muted')} />
                    {!rail && <span className="truncate">{it.label}</span>}
                    {it.badge && <NavBadge kind={it.badge} rail={rail} />}
                  </Link>
                )
                return (
                  <div key={it.to} className="flex flex-col gap-px">
                    {rail ? <Tooltip content={it.label} side="right">{link}</Tooltip> : link}
                    {!rail && inSection && it.children && it.children.length > 0 && (
                      <div className="relative mt-px mb-1 ml-[18px] flex flex-col gap-px border-l border-border pl-2">
                        {it.children.map(c => {
                          const on = child === c.to
                          return (
                            <Link key={c.to} to={c.to} onClick={onNavigate} aria-current={on ? 'page' : undefined}
                              className={cn('relative flex h-7 items-center rounded-[6px] px-2 text-base transition-colors outline-none focus-visible:ring-2 focus-visible:ring-focus',
                                on ? 'bg-chrome-active font-medium text-text shadow-[0_0_0_1px_var(--border)]' : 'text-text-muted hover:bg-chrome-hover hover:text-text')}>
                              {on && <span aria-hidden className="absolute top-2 bottom-2 -left-[9px] w-px bg-primary" />}
                              <span className="truncate">{c.label}</span>
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
      <div className={cn('flex flex-col gap-2 pb-3', rail ? 'px-2' : 'px-3')}>
        {!rail && footer}
        {onToggleCollapsed && !drawer && (
          <Tooltip content={rail ? 'Expand sidebar' : 'Collapse sidebar'} shortcut="[" side="right">
            <button type="button" onClick={onToggleCollapsed} aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn('flex h-8 items-center gap-2 rounded-control text-sm text-text-subtle transition-colors hover:bg-chrome-hover hover:text-text focus-visible:outline-2 focus-visible:outline-focus', rail ? 'w-10 justify-center' : 'px-2')}>
              {rail ? <PanelLeftOpen className="size-[18px]" /> : <PanelLeftClose className="size-[18px]" />}
              {!rail && <span className="flex flex-1 items-center justify-between">Collapse <Kbd>[</Kbd></span>}
            </button>
          </Tooltip>
        )}
      </div>
    </nav>
  )
}

/** Small plan card for the sidebar footer. */
export function PlanCard() {
  const { store, can } = useSession()
  if (!store || !can('billing')) return null
  const free = /free/i.test(store.plan.code) || /free/i.test(store.plan.name)
  return (
    <Link to="/settings/plan" className="group flex items-center gap-2 rounded-card border border-border bg-surface/70 px-3 py-2 transition-colors hover:border-border-strong hover:bg-surface focus-visible:outline-2 focus-visible:outline-focus dark:bg-white/[0.02]">
      <span className="grid size-7 shrink-0 place-items-center rounded-[8px] bg-gradient-to-br from-primary to-accent text-white"><Sparkles className="size-3.5" /></span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-text">{store.plan.name} plan</span>
        <span className="truncate text-xs text-text-muted">{free ? 'Upgrade for more' : 'Manage billing'}</span>
      </span>
    </Link>
  )
}

/** Live count beside a nav item (inbox: open conversations with unread customer messages). Hidden at 0. */
function NavBadge({ kind, rail }: { kind: 'inbox_unread'; rail: boolean }) {
  const { can, store } = useSession()
  const qc = useQueryClient()
  const enabled = !!store && kind === 'inbox_unread' && can('inbox')
  const key = ['inbox', 'unread', store?.id ?? null]
  const q = useQuery({
    queryKey: key, enabled, refetchInterval: 60_000, staleTime: 15_000,
    queryFn: async ({ signal }) => { const r = await api.get<{ count: number }>('/api/admin/inbox/unread', { signal }); return Math.max(0, Number(r?.count) || 0) },
  })
  useStoreEvents(e => { if (e.type.startsWith('conversation')) void qc.invalidateQueries({ queryKey: key }) }, { enabled, invalidate: false })
  const n = q.data ?? 0
  if (!enabled || n <= 0) return null
  const text = n > 99 ? '99+' : String(n)
  // The count is read with the link ("Inbox, 3 unread"); the dot / pill itself is decoration.
  return rail
    ? <><span aria-hidden className="absolute right-1 top-1 size-2 rounded-full bg-primary" data-nav-badge={text} /><span className="sr-only">, {text} unread</span></>
    : <><span aria-hidden className="ms-auto min-w-5 rounded-full bg-primary px-1.5 text-center text-xs font-semibold leading-5 text-primary-foreground tabular" data-nav-badge={text}>{text}</span><span className="sr-only">, {text} unread</span></>
}
